/**
 *  Copyright (C) 2022 - Jeci SARL - https://jeci.fr
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU Affero General Public License as
 *  published by the Free Software Foundation, either version 3 of the
 *  License, or (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU Affero General Public License for more details.
 *
 *  You should have received a copy of the GNU Affero General Public License
 *  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import configService from "./ConfigService.js";
import { nodesApi } from "../AlfrescoApi.js";
import { useConfigStore } from "@/stores/config.js";

const PRISTY_CONFIG_NODE_ID = "pristy-config";

/**
 * Service for managing vue-*.json configuration files.
 * Follows the same pattern as ClassificationService.
 */
class ViewConfigService {
  /**
   * Fetches a specific view configuration file.
   * @param {string} filename The name of the view file (e.g., 'vue-exec-pieces.json').
   * @returns {Promise<Object>} A promise that resolves with the JSON content of the view.
   */
  getView(filename) {
    const configStore = useConfigStore();
    return configService
      .getConfig(configStore.INSTANCE_NAME, filename)
      .then((response) => response.jsonContent);
  }

  /**
   * Saves a view configuration file.
   * @param {string} filename The name of the view file.
   * @param {Object} content The view configuration object to save.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  saveView(filename, content) {
    const configStore = useConfigStore();
    return configService.updateConfig(
      configStore.INSTANCE_NAME,
      filename,
      content,
    );
  }

  /**
   * Deletes a view configuration file by saving null/empty content.
   * Note: Actual deletion depends on the backend implementation.
   * @param {string} filename The name of the view file to delete.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  deleteView(filename) {
    const configStore = useConfigStore();
    return configService.updateConfig(
      configStore.INSTANCE_NAME,
      filename,
      null,
    );
  }

  /**
   * Lists all existing view configuration file names.
   * @returns {Promise<string[]>} A promise that resolves with an array of filenames.
   */
  async listViews() {
    const children = await this.#listInstanceChildren(["properties"]);
    return children
      .filter(
        (entry) =>
          entry.name.startsWith("vue-") && entry.name.endsWith(".json"),
      )
      .map((entry) => entry.name);
  }

  /**
   * Lists all existing view configuration entries (full Alfresco nodes).
   * @returns {Promise<Object[]>} A promise that resolves with an array of Alfresco entry objects.
   */
  async listViewEntries() {
    const children = await this.#listInstanceChildren([
      "properties",
      "allowableOperations",
    ]);
    return children.filter(
      (entry) => entry.name.startsWith("vue-") && entry.name.endsWith(".json"),
    );
  }

  async #listInstanceChildren(include) {
    const configStore = useConfigStore();
    try {
      const children = await nodesApi.listNodeChildren(PRISTY_CONFIG_NODE_ID, {
        include,
      });
      const instanceFolder = children.list.entries.find(
        (e) => e.entry.name === configStore.INSTANCE_NAME,
      );
      if (!instanceFolder) {
        return [];
      }
      const instanceChildren = await nodesApi.listNodeChildren(
        instanceFolder.entry.id,
        { include },
      );
      return instanceChildren.list.entries.map((entry) => entry.entry);
    } catch {
      return [];
    }
  }
}

export default new ViewConfigService();
